import { useEffect, useState } from 'react';
import { pc, pc1 } from './format';

// Drawn around the origin; the viewBox leaves room for the "pace" label outside the
// ring and the 0% / 100% labels under its ends, so nothing spills onto the card.
const VIEWBOX = '-200 -172 400 300';
const R0 = 112;
const R1 = 136;
const R = (R0 + R1) / 2;
const A0 = -0.75 * Math.PI;
const A1 = 0.75 * Math.PI;
/** The needle is a short pointer under the percentage, so it never crosses the text. */
const HUB_Y = 50;
const NEEDLE = 30;

const clamp01 = (t: number) => Math.min(Math.max(t, 0), 1);
const ang = (t: number) => A0 + (A1 - A0) * clamp01(t);
/** Angles run clockwise from 12 o'clock, like d3.arc. */
const pt = (r: number, a: number): [number, number] => [r * Math.sin(a), -r * Math.cos(a)];

/** The band as a flat-ended stroke along its centre line, so small values stay slim. */
function arcPath(a0: number, a1: number): string {
  const [x0, y0] = pt(R, a0);
  const [x1, y1] = pt(R, a1);
  return `M ${x0} ${y0} A ${R} ${R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
}

let animatedOnce = false;

/** Speedometer of overall progress, with a tick where today's pace says it should be. */
export default function Gauge({ value, pace }: { value: number; pace: number }) {
  const [shown, setShown] = useState(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return animatedOnce || reduce ? value : 0;
  });

  useEffect(() => {
    if (animatedOnce || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(value);
      return;
    }
    animatedOnce = true;
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 1400);
      setShown(value * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  const band = R1 - R0;
  const pa = ang(pace);
  const [px0, py0] = pt(R0 - 4, pa);
  const [px1, py1] = pt(R1 + 6, pa);
  const [plx, ply] = pt(R1 + 14, pa);
  const va = ang(shown);
  const [ex, ey] = pt(R, A1);

  return (
    <svg viewBox={VIEWBOX} width="100%" role="img" aria-label={'Overall progress ' + pc(value)}>
      <defs>
        <linearGradient id="skGaugeFill" x1="0" x2="1">
          <stop offset="0" stopColor="#f3dc92" />
          <stop offset=".45" stopColor="#ef7a1a" />
          <stop offset="1" stopColor="#d8261a" />
        </linearGradient>
      </defs>
      <path d={arcPath(A0, A1)} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={band} />
      {shown > 0.0005 && <path d={arcPath(A0, va)} fill="none" stroke="url(#skGaugeFill)" strokeWidth={band} />}
      {Array.from({ length: 11 }, (_, i) => {
        const a = ang(i / 10);
        const major = i % 5 === 0;
        const [x1, y1] = pt(R0 - (major ? 20 : 14), a);
        const [x2, y2] = pt(R0 - 8, a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(245,240,232,.35)" strokeWidth={major ? 2 : 1} />;
      })}
      <text className="g-tick" x={0} y={-(R0 - 34)} textAnchor="middle">
        50%
      </text>
      <text className="g-tick" x={-ex} y={ey + band / 2 + 16} textAnchor="middle">
        0%
      </text>
      <text className="g-tick" x={ex} y={ey + band / 2 + 16} textAnchor="middle">
        100%
      </text>
      <line x1={px0} y1={py0} x2={px1} y2={py1} stroke="#f5f0e8" strokeWidth={2.5} />
      <text
        className="g-sub"
        x={plx}
        y={ply + 4}
        textAnchor={Math.abs(Math.sin(pa)) < 0.2 ? 'middle' : Math.sin(pa) > 0 ? 'start' : 'end'}
      >
        pace
      </text>
      <text className="g-sub" y={-34} textAnchor="middle">
        overall progress
      </text>
      <text className="g-pct" y={12} textAnchor="middle" fontSize={40}>
        {pc1(value)}
      </text>
      <g transform={`translate(0,${HUB_Y}) rotate(${(va * 180) / Math.PI})`}>
        <path d={`M -4 0 L 0 ${-NEEDLE} L 4 0 Z`} fill="#f5f0e8" />
        <circle r={7} fill="#0b2a22" stroke="#f5f0e8" strokeWidth={3} />
      </g>
    </svg>
  );
}
